import React, { useEffect, useMemo, useState } from 'react'
import { Alert, Badge, Button, Card, Empty, Space, Spin, Tag, Typography, message } from 'antd'
import { CodeOutlined, DashboardOutlined, ReloadOutlined, SafetyOutlined } from '@ant-design/icons'
import { getAicProfileProjects } from '../../request'
import AicCodexOnboardingTab from './AicCodexOnboardingTab'
import { VIBE_DATASET_BOOTSTRAP_PROMPT } from './vibeDatasetBootstrapPrompt'

const { Title, Text, Paragraph } = Typography

const STAGE_META = {
    registered: { label: '已登记', color: 'blue' },
    ready: { label: '待首次接入', color: 'orange' },
    published: { label: '已发布', color: 'green' },
    failed: { label: '接入异常', color: 'red' },
}

function getProjects(value) {
    return Array.isArray(value) ? value : (value?.list || value?.data || [])
}

function getStage(project) {
    if (project?.status === 'failed') return STAGE_META.failed
    if (project?.dataset?.lastSuccessfulUploadAt || project?.lastSuccessfulUploadAt) return STAGE_META.published
    if (project?.dataset || project?.isDataset || project?.projectKind === 'dataset') return STAGE_META.ready
    return STAGE_META.registered
}

function copyText(value) {
    if (!value) {
        message.warning('暂无可复制内容')
        return
    }
    if (typeof navigator !== 'undefined' && navigator.clipboard?.writeText) {
        navigator.clipboard.writeText(value)
            .then(() => message.success('已复制到剪贴板'))
            .catch(() => message.warning('复制失败，请手动复制'))
        return
    }
    message.warning('当前浏览器不支持自动复制，请手动复制')
}

function AicProjectCard({ project, onDashboard }) {
    const stage = getStage(project)
    const dataset = project.dataset || project
    return (
        <Card size="small" className="profile-aic-project-card" bordered={false}>
            <div className="profile-aic-project-head">
                <div>
                    <Title level={4}>{project.projectName || project.name || '-'}</Title>
                    <Text type="secondary">{dataset.ownerDepartmentName || project.businessSubject || '未设置归属部门'}</Text>
                </div>
                <Badge color={stage.color} text={stage.label} />
            </div>
            <Space wrap size={[8, 4]}>
                {dataset.sensitivityLevel && <Tag color="purple">{dataset.sensitivityLevel}</Tag>}
                {dataset.updateFrequency && <Tag>{dataset.updateFrequency}</Tag>}
                <Text type="secondary">最近成功发布：{dataset.lastSuccessfulUploadAt || '从未上传过数据'}</Text>
            </Space>
            <div className="profile-aic-project-actions">
                <Button type="link" icon={<CodeOutlined />} onClick={() => copyText(`使用 $aic-data-ingestion，把我选择的文件接入 AIC 项目 ${project.id}（${project.projectName || project.name || ''}）。`) }>复制 Codex 更新提示词</Button>
                <Button type="link" icon={<DashboardOutlined />} onClick={() => onDashboard(project.id)}>查看数据看板</Button>
            </div>
        </Card>
    )
}

export default function AicDataAssetTab({ onTabSwitch, history }) {
    const [loading, setLoading] = useState(true)
    const [error, setError] = useState('')
    const [projects, setProjects] = useState([])
    const load = () => {
        setLoading(true)
        setError('')
        getAicProfileProjects({})
            .then(value => setProjects(getProjects(value)))
            .catch(() => setError('AIC 项目暂时无法读取，请稍后刷新'))
            .finally(() => setLoading(false))
    }
    useEffect(load, [])
    const hasProjects = projects.length > 0
    const dashboard = id => history?.push(`/portal/aic-dashboard/datasets/${encodeURIComponent(id)}`)
    const nextAction = useMemo(() => hasProjects ? '选择一个项目，在 Codex 中分析并确认数据后发布' : '准备 AIC 规划 Markdown，在 Codex 中完成数据资产登记', [hasProjects])

    return (
        <div className="profile-aic-assets">
            <section className="profile-aic-assets-hero">
                <Tag color="cyan">AIC · All In Context</Tag>
                <Title level={1}>AIC 数据资产</Title>
                <Paragraph>在 Codex 中分析、确认和发布业务数据。此页面用于查看状态和配置工作台，不直接创建项目或上传业务数据。</Paragraph>
                <Alert type="info" showIcon icon={<SafetyOutlined />} message={`下一步：${nextAction}`} />
            </section>
            <section className="profile-aic-assets-flow">
                {['规划与登记', '项目与权限', '数据接入', '发布与监控'].map((item, index) => <div key={item} className="profile-aic-flow-item"><span>{index + 1}</span><Text>{item}</Text>{index < 3 && <b>→</b>}</div>)}
            </section>
            <section className="profile-aic-assets-section">
                <div className="profile-aic-assets-section-head"><div><Title level={2}>我的 AIC 项目</Title><Text type="secondary">项目登记、数据分析和发布均在 Codex 中完成。</Text></div><Button icon={<ReloadOutlined />} onClick={load}>刷新状态</Button></div>
                {error && <Alert type="warning" showIcon message={error} />}
                <Spin spinning={loading}>{hasProjects ? <div className="profile-aic-project-grid">{projects.map(project => <AicProjectCard key={project.id} project={project} onDashboard={dashboard} />)}</div> : !loading && <Empty description="还没有 AIC 数据资产"><Button type="primary" icon={<CodeOutlined />} onClick={() => copyText(VIBE_DATASET_BOOTSTRAP_PROMPT)}>复制 Codex 登记提示词</Button></Empty>}</Spin>
            </section>
            <section className="profile-aic-assets-section">
                <Title level={2}>Codex 工作台</Title>
                <Text type="secondary">下载一次、长期复用。复杂业务数据由 Codex 本地分析，确认后再通过受控接口发布。</Text>
                <AicCodexOnboardingTab onTabSwitch={onTabSwitch} />
            </section>
        </div>
    )
}
